import React from 'react';
import PropTypes from 'prop-types';

import { Button, Modal, Icon } from '@weezevent/nacre';
import './onboardingModalTour.scss';
import { useSegment } from '../../components/tracking/trackingProvider';

function PrimaryLabel({ index, isLastStep, labelStart, labelFinish, labelNext }) {
    const labelPrimary = React.useMemo(() => {
        if (index === 0 && labelStart) {
            return labelStart;
        }

        if (isLastStep && labelFinish) {
            return labelFinish;
        }

        return labelNext;
    }, [index, isLastStep, labelStart, labelFinish, labelNext]);

    return (
        <span className={'wz-onboarding-primary-label'}>
            <span>{labelPrimary}</span>
            {index !== 0 && !isLastStep && <Icon className={'wz-onboarding-primary-label__icon'} name="chevron-right" />}
        </span>
    );
}

PrimaryLabel.defaultProps = {
    isLastStep: false,
    index: 0,
    labelStart: null,
    labelFinish: null,
    labelNext: null
};

PrimaryLabel.propTypes = {
    isLastStep: PropTypes.bool,
    index: PropTypes.number,
    labelStart: PropTypes.node,
    labelFinish: PropTypes.node,
    labelNext: PropTypes.node
};

function StepsIndicator({ index, stepsCount }) {
    const Steps = React.useMemo(() => {
        let _steps = [];
        for (let i = 0; i < stepsCount; i++) {
            _steps.push(<div key={i} className={`wz-step-indicator ${index === i ? 'wz-step-indicator__active' : ''}`} />);
        }

        return _steps;
    }, [index, stepsCount]);

    return <div className={'wz-step-indicator__container'}>{Steps}</div>;
}

StepsIndicator.defaultProps = {
    stepsCount: 0,
    index: 0
};

StepsIndicator.propTypes = {
    stepsCount: PropTypes.number,
    index: PropTypes.number
};

function OnboardingStep({ step }) {
    if (!step) {
        return null;
    }

    return (
        <div className={'wz-onboarding-step'}>
            {step.imgSrc && <img className={'wz-onboarding-step__img'} src={step.imgSrc} alt={step.stepName || 'preview'} />}
            {step.content && <div className={'wz-onboarding-step__content'}>{step.content}</div>}
        </div>
    );
}

OnboardingStep.defaultProps = {
    step: null
};

OnboardingStep.propTypes = {
    step: PropTypes.object
};

export default function OnboardingModalTour({ onStart, onFinished, steps, isMobile, modalProps, labelCTAProps, className }) {
    const [displayOnboarding, setDisplayOnboarding] = React.useState(true);
    const [index, setIndex] = React.useState(0);
    const { track } = useSegment();

    const stepsCount = React.useMemo(() => {
        return steps.length;
    }, [steps]);

    const _onClose = React.useCallback(() => {
        track(
            `ACTION > EXIT ONBOARDING ${index + 1}`,
            {
                'Onboarding step': steps[index].stepName,
                'Page Title': 'MODALE ONBOARDING NBO'
            },
            {
                'Onboarding end date': new Date()
            }
        );
        setDisplayOnboarding(false);
        onFinished();
    }, [index, steps, onFinished]);

    const onFindOutMore = React.useCallback(() => {
        track(`ACTION > LEARN MORE ONBOARDING ${index + 1}`, {
            'Onboarding step': steps[index].stepName,
            'Page Title': 'MODALE ONBOARDING NBO'
        });
    }, [index, steps]);

    const onNextStep = React.useCallback(() => {
        track(
            `ACTION > VALIDATE STEP ONBOARDING ${index + 1}`,
            {
                'Onboarding step': steps[index].stepName,
                'Page Title': 'MODALE ONBOARDING NBO'
            },
            index === steps.length - 1
                ? {
                      'Onboarding end date': new Date()
                  }
                : {}
        );
        if (index === stepsCount - 1) {
            return _onClose();
        }

        setIndex(currentState => {
            return currentState + 1;
        });
    }, [index, _onClose, stepsCount, steps]);

    const onPreviousStep = React.useCallback(() => {
        setIndex(currentState => {
            if (currentState === 0) {
                return currentState;
            }

            return currentState - 1;
        });
    }, []);

    return (
        <Modal
            className={`wz-onboarding-modal-tour ${className}`}
            size={isMobile ? 'fullscreen' : 'large'}
            allowExternalClosing={false}
            open={displayOnboarding}
            onClose={_onClose}
            title={<span className={`wz-onboarding-modal-tour__title`}>{steps[index].title}</span>}
            subtitle={<span className={`wz-onboarding-modal-tour__subtitle`}>{steps[index].subtitle}</span>}
            customFooter={
                <div className={`wz-onboarding-modal-tour__footer`}>
                    <StepsIndicator index={index} stepsCount={stepsCount} />
                    <Button.Group>
                        {Boolean(steps[index]?.href) && (
                            <Button
                                onClick={onFindOutMore}
                                className={`wz-onboarding-modal-tour__find-out-more`}
                                as={'a'}
                                href={steps[index]?.href}
                                target={'_blank'}
                                primary
                                inverted
                                label={labelCTAProps.findOutMore}
                            />
                        )}
                        {index !== 0 && labelCTAProps.back && <Button onClick={onPreviousStep} label={labelCTAProps.back} />}
                        <Button
                            primary
                            onClick={onNextStep}
                            label={
                                <PrimaryLabel
                                    index={index}
                                    isLastStep={index === stepsCount - 1}
                                    labelStart={labelCTAProps.start}
                                    labelFinish={labelCTAProps.last}
                                    labelNext={labelCTAProps.next}
                                />
                            }
                        />
                    </Button.Group>
                </div>
            }
        >
            <OnboardingStep step={steps[index]} />
        </Modal>
    );
}

OnboardingModalTour.defaultProps = {
    onStart: () => {},
    onFinished: () => {},
    steps: [],
    modalProps: {
        title: 'title',
        subtitle: 'subtitle'
    },
    labelCTAProps: {
        start: 'Start',
        next: 'Next',
        back: 'Back',
        last: 'Finish',
        findOutMore: 'Find out more'
    },
    className: ''
};

OnboardingModalTour.propTypes = {
    onStart: PropTypes.func,
    onFinished: PropTypes.func,
    steps: PropTypes.array,
    isMobile: PropTypes.bool,
    modalProps: PropTypes.object,
    labelCTAProps: PropTypes.object,
    children: PropTypes.node,
    className: PropTypes.string
};
